import { Document } from '@contentful/rich-text-types';
import { queryOptions } from '@tanstack/react-query';

import { API_ENDPOINT } from '~/config';

export type ContentfulReasonsToSwitch = {
  Fields: {
    title: string;
    content: Document;
    reasons: Document[];
  };
};

export type ContentfulStressFreeSwitching = {
  Fields: {
    title: string;
    content: Document;
    steps: Document[];
  };
};

export type ContentfulFAQ = {
  Fields: {
    question: Document;
    answer: Document;
    questionNumber: number;
  };
}[];

export type ContentfulBottomAwards = {
  Fields: {
    title: string;
    content: Document;
  };
};

export type ContentfulErrorPage = {
  Fields: {
    title: string;
    content: Document;
  };
};

export type ContentfulPageAsset = {
  Fields: {
    heading?: Document;
    body?: Document;
    slug: string;
    images?: Array<{
      sys: {
        id: string;
        linkType: string;
        type: string;
      };
    }>;
  };
};

export type ContentfulPageButton = {
  Fields: {
    slug: string;
    text: string;
  };
};

export const getContentfulReasonsToSwitch = queryOptions({
  queryKey: ['getContentfulReasonsToSwitch'],
  queryFn: async (): Promise<ContentfulReasonsToSwitch> => {
    try {
      const response = await fetch(
        `${API_ENDPOINT}/content/psuReasonsToSwitch`,
      );

      if (!response.ok) {
        throw new Error(
          `Error fetching reasons to switch content: ${response.statusText}`,
        );
      }

      const content = (await response.json()) as ContentfulReasonsToSwitch;
      return content;
    } catch (error) {
      console.error('Error fetching reasons to switch content:', error);
      return {
        Fields: {
          title: 'Default Reasons Title',
          content: {} as Document,
          reasons: [],
        },
      };
    }
  },
});

export const getContentfulStressFreeSwitching = queryOptions({
  queryKey: ['getContentfulStressFreeSwitching'],
  queryFn: async (): Promise<ContentfulStressFreeSwitching> => {
    try {
      const response = await fetch(
        `${API_ENDPOINT}/content/psuStressFreeSwitching`,
      );

      if (!response.ok) {
        throw new Error(
          `Error fetching stress free switching content: ${response.statusText}`,
        );
      }

      const content = (await response.json()) as ContentfulStressFreeSwitching;
      return content;
    } catch (error) {
      console.error('Error fetching stress free switching content:', error);
      return {
        Fields: {
          title: 'Default Stress Free Title',
          content: {} as Document,
          steps: [],
        },
      };
    }
  },
});

export const getContentfulFAQ = queryOptions({
  queryKey: ['getContentfulFaq'],
  queryFn: async (): Promise<ContentfulFAQ> => {
    try {
      const response = await fetch(`${API_ENDPOINT}/content/psuFaq`);

      if (!response.ok) {
        throw new Error(`Error fetching FAQ content: ${response.statusText}`);
      }

      const content = (await response.json()) as ContentfulFAQ;
      return content;
    } catch (error) {
      console.error('Error fetching FAQ content:', error);
      return [];
    }
  },
});

export const getContentfulBottomAwards = queryOptions({
  queryKey: ['getContentfulBottomAwards'],
  queryFn: async (): Promise<ContentfulBottomAwards> => {
    try {
      const response = await fetch(`${API_ENDPOINT}/content/psuBottomAwards`);

      if (!response.ok) {
        throw new Error(
          `Error fetching bottom awards content: ${response.statusText}`,
        );
      }

      const content = (await response.json()) as ContentfulBottomAwards;
      return content;
    } catch (error) {
      console.error('Error fetching bottom awards content:', error);
      return {
        Fields: {
          title: 'Default Awards Title',
          content: {} as Document,
        },
      };
    }
  },
});

export const getContentfulErrorPage = queryOptions({
  queryKey: ['getContentfulErrorPage'],
  queryFn: async (): Promise<ContentfulErrorPage> => {
    try {
      const response = await fetch(`${API_ENDPOINT}/content/psuErrorPage`);

      if (!response.ok) {
        throw new Error(
          `Error fetching error page content: ${response.statusText}`,
        );
      }

      const content = (await response.json()) as ContentfulErrorPage;
      return content;
    } catch (error) {
      console.error('Error fetching error page content:', error);
      return {
        Fields: {
          title: 'Default Error Title',
          content: {} as Document,
        },
      };
    }
  },
});

export const getContentfulPageAssetBySlug = (slug: string) =>
  queryOptions({
    queryKey: ['getContentfulPageAsset', slug],
    queryFn: async (): Promise<ContentfulPageAsset> => {
      try {
        const response = await fetch(
          `${API_ENDPOINT}/content/psuPageAsset/${slug}`,
        );

        if (!response.ok) {
          throw new Error(
            `Error fetching page asset content: ${response.statusText}`,
          );
        }

        const content = (await response.json()) as ContentfulPageAsset;
        return content;
      } catch (error) {
        console.error('Error fetching page asset content:', error);
        return {
          Fields: {
            heading: {} as Document,
            body: {} as Document,
            slug: slug,
            images: [],
          },
        };
      }
    },
  });

export const getContentfulButtonTextBySlug = (slug: string) =>
  queryOptions({
    queryKey: ['getContentfulButtonText', slug],
    queryFn: async (): Promise<ContentfulPageButton> => {
      try {
        const response = await fetch(
          `${API_ENDPOINT}/content/psuPageButton/${slug}`,
        );

        if (!response.ok) {
          throw new Error(
            `Error fetching page button content: ${response.statusText}`,
          );
        }

        const content = (await response.json()) as ContentfulPageButton;
        return content;
      } catch (error) {
        console.error('Error fetching page asset content:', error);
        return {
          Fields: {
            text: '',
            slug: slug,
          },
        };
      }
    },
  });
